import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { AppFooter } from "../components/AppFooter";
import { InstallGuide } from "../components/InstallGuide";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/clubs");
    } catch {
      setError("Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-dvh bg-chalk-50">
      <div className="hero-fairway flex flex-col items-center justify-end px-6 pb-14 pt-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8 text-chalk-50">
            <path d="M6 21V4l12 5.5L6 15" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-chalk-50">ClubConnect</h1>
        <p className="mt-1.5 text-sm text-turf-100/80">Dein Golfclub, direkt am Platz</p>
      </div>

      <div className="mx-auto -mt-8 max-w-md px-6 pb-12">
        <form
          onSubmit={handleSubmit}
          className="card animate-fade-up space-y-4 rounded-3xl p-6"
          noValidate
        >
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink-900">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 w-full rounded-xl border border-chalk-200 bg-chalk-50 px-4 text-base outline-none transition-colors focus:border-turf-600 focus:bg-white"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-ink-900">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full rounded-xl border border-chalk-200 bg-chalk-50 px-4 text-base outline-none transition-colors focus:border-turf-600 focus:bg-white"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-turf-700 text-base font-semibold text-chalk-50 shadow-sm transition-all hover:bg-turf-800 active:scale-[0.985] disabled:opacity-60"
          >
            {submitting ? "Anmelden…" : "Anmelden"}
          </button>
        </form>

      <Link
        to="/register"
        className="mt-6 block text-center text-sm font-medium text-turf-700 underline-offset-2 hover:underline"
      >
        Noch kein Konto? Registrieren
      </Link>

      <Link
        to="/admin/login"
        className="mt-3 block text-center text-sm text-ink-400 underline-offset-2 hover:underline"
      >
        Admin-Login
      </Link>

      <button
        type="button"
        onClick={() => setShowInstallGuide(true)}
        className="mt-6 flex w-full items-center justify-center gap-1.5 text-center text-sm font-medium text-turf-700"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
          <rect x="6" y="3" width="12" height="18" rx="2.5" />
          <path d="M11 18h2" strokeLinecap="round" />
        </svg>
        App aufs Handy holen – so geht's
      </button>

      <AppFooter />
      </div>

      {showInstallGuide && <InstallGuide onClose={() => setShowInstallGuide(false)} />}
    </main>
  );
}
